import * as turf from '@turf/turf'
import type { Polygon } from 'geojson'

type Feature<_T = any, _P = any> = ReturnType<typeof turf.polygon>
type Properties = Record<string, any>

/**
 * Calculate the Minimum Bounding Rectangle (MBR) for a polygon
 * This provides a more accurate representation of irregular shapes
 * compared to an Axis-Aligned Bounding Box (AABB)
 */
export const calculateMBR = (polygon: Feature<Polygon, Properties>) => {
  // Get the convex hull first to simplify the shape
  const hull = turf.convex(polygon)

  if (!hull) {
    throw new Error('Could not calculate convex hull for polygon')
  }

  // Extract coordinates from the hull

  // Variables to track the minimum area rectangle
  let minArea = Infinity
  let minRect = null
  let bestAngle = 0
  let bestWidth = 0
  let bestLength = 0

  // Try different rotation angles to find the minimum area
  // We'll check every degree from 0 to 90 (rectangles have 90° symmetry)
  for (let angle = 0; angle < 90; angle += 1) {
    // Rotate the polygon
    const rotated = turf.transformRotate(hull, angle)

    // Get the axis-aligned bounding box of the rotated polygon
    const bbox = turf.bbox(rotated)

    // Calculate dimensions of this bounding box
    const width = bbox[2] - bbox[0] // max_x - min_x
    const height = bbox[3] - bbox[1] // max_y - min_y

    // Calculate area (in the rotated coordinate system)
    const area = width * height

    // If this is the smallest area so far, save it
    if (area < minArea) {
      minArea = area
      bestAngle = angle

      // Create the bounding box polygon
      const bboxPoly = turf.bboxPolygon(bbox)

      // Rotate it back to the original orientation
      minRect = turf.transformRotate(bboxPoly, -angle)

      // Convert width/height to actual distances in meters
      const corner1 = [bbox[0], bbox[1]]
      const corner2 = [bbox[2], bbox[1]]
      const corner3 = [bbox[0], bbox[3]]

      bestWidth = turf.distance(corner1, corner2, { units: 'meters' })
      bestLength = turf.distance(corner1, corner3, { units: 'meters' })
    }
  }

  // Ensure length is always the longer dimension
  if (bestWidth > bestLength) {
    ;[bestLength, bestWidth] = [bestWidth, bestLength]
    bestAngle = (bestAngle + 90) % 180
  }

  return {
    rectangle: minRect,
    angle: bestAngle,
    length: bestLength,
    width: bestWidth,
    area: minArea,
  }
}

/**
 * Calculate shape metrics using the MBR
 */
export const calculateShapeMetricsWithMBR = (
  polygon: Feature<Polygon, Properties>,
  mbr: ReturnType<typeof calculateMBR>
) => {
  // Get the actual area of the polygon
  const polygonArea = turf.area(polygon)

  // Get perimeter
  const line = turf.polygonToLine(polygon)
  const perimeter = turf.length(line, { units: 'meters' }) * 1000 // Convert km to m

  // Calculate MBR area for comparison
  const mbrArea = mbr.length * mbr.width

  // Shape factor using MBR (how well the shape fills its MBR)
  // 1.0 = perfect rectangle, lower values = more irregular
  const rectangularity = polygonArea / mbrArea

  // Aspect ratio from MBR
  const aspectRatio = mbr.length / mbr.width

  // Circularity (Polsby-Popper score)
  const circularity = (4 * Math.PI * polygonArea) / (perimeter * perimeter)

  // Determine orientation based on MBR angle
  let orientation = 'North-South'
  const normalizedAngle = mbr.angle % 180

  if (normalizedAngle > 67.5 && normalizedAngle < 112.5) {
    orientation = 'East-West'
  } else if (normalizedAngle > 22.5 && normalizedAngle < 67.5) {
    orientation = 'Northeast-Southwest'
  } else if (normalizedAngle > 112.5 && normalizedAngle < 157.5) {
    orientation = 'Northwest-Southeast'
  }

  // If nearly square, override orientation
  if (aspectRatio < 1.2) {
    orientation = 'Square'
  }

  return {
    rectangularity,
    circularity,
    aspectRatio,
    orientation,
    mbrEfficiency: rectangularity, // How efficiently the shape uses its MBR
    regularity:
      rectangularity > 0.85
        ? 'Regular'
        : rectangularity > 0.7
          ? 'Moderate'
          : 'Irregular',
  }
}
